/**
 * Insurance (Ladder) Backend API Service
 * Reuses the same `insurance` backend the mobile app uses (ladder.wavvest.com).
 * Auth: AWS IAM (SigV4) via the Cognito Identity Pool auth role — the API
 * Gateway in fo-ladder-backend uses an IAM authorizer. InsuranceAPI is
 * therefore registered in aws-config.js *with* a `region`, which is what makes
 * Amplify SigV4-sign these requests. Do not add an Authorization header here.
 */
import { get, post, put, patch } from "aws-amplify/api";
import type {
  QuoterBody,
  QuoterResponse,
  CoverageBody,
  CoverageResponse,
  ApplicationBody,
  ApplicationCreationResponse,
  ApplicationResponse,
  PolicyResponse,
} from "@/types/insurance";

const API_NAME = "InsuranceAPI";

// Amplify types the REST `body` option as DocumentType, which a named interface
// isn't assignable to without an index signature. Our payloads are plain JSON,
// so cast them to a JSON-compatible type at the call boundary.
type JsonValue =
  | string
  | number
  | boolean
  | null
  | JsonValue[]
  | { [key: string]: JsonValue };
const asBody = (payload: object): JsonValue => payload as unknown as JsonValue;

/**
 * Coverage-needs calculator. Optional in the core flow (which defaults coverage
 * to $1M) — the response's `nearest_policy` seeds the coverage selector.
 */
export async function calculateCoverage(
  payload: CoverageBody,
): Promise<CoverageResponse> {
  const response = await post({
    apiName: API_NAME,
    path: "/api/calculate_coverage",
    options: { body: asBody(payload) },
  });
  const result = await response.response;
  const body = await result.body.json();
  return body as unknown as CoverageResponse;
}

/** Create a new quote for the current user. */
export async function createQuote(
  payload: QuoterBody,
): Promise<QuoterResponse> {
  const response = await put({
    apiName: API_NAME,
    path: "/api/create_quoter",
    options: { body: asBody(payload) },
  });
  const result = await response.response;
  const body = await result.body.json();
  return body as unknown as QuoterResponse;
}

/**
 * Retrieve the current user's existing quote.
 * Throws with a 404 status code when no quote exists yet — callers should treat
 * that as "no quote" and create one, rather than as a failure.
 */
export async function retrieveQuote(): Promise<QuoterResponse> {
  const response = await get({
    apiName: API_NAME,
    path: "/api/retrieve_quoter",
  });
  const result = await response.response;
  const body = await result.body.json();
  return body as unknown as QuoterResponse;
}

/** Update the current user's quote (e.g. with the finalized quote inputs). */
export async function updateQuote(
  payload: QuoterBody,
): Promise<QuoterResponse> {
  const response = await patch({
    apiName: API_NAME,
    path: "/api/update_quoter",
    options: { body: asBody(payload) },
  });
  const result = await response.response;
  const body = await result.body.json();
  return body as unknown as QuoterResponse;
}

/**
 * Start an application. Returns a Ladder-hosted application URL (valid until
 * `url_expires`) that the user completes off-site.
 */
export async function createApplication(
  payload: ApplicationBody,
): Promise<ApplicationCreationResponse> {
  const response = await put({
    apiName: API_NAME,
    path: "/api/start_application",
    options: { body: asBody(payload) },
  });
  const result = await response.response;
  const body = await result.body.json();
  return body as unknown as ApplicationCreationResponse;
}

/** Retrieve the current user's application status. */
export async function retrieveApplication(): Promise<ApplicationResponse> {
  const response = await get({
    apiName: API_NAME,
    path: "/api/retrieve_application_status",
  });
  const result = await response.response;
  const body = await result.body.json();
  return body as unknown as ApplicationResponse;
}

/** Retrieve the current user's active policies. */
export async function retrievePolicies(): Promise<PolicyResponse> {
  const response = await get({
    apiName: API_NAME,
    path: "/api/retrieve_policy_info",
  });
  const result = await response.response;
  const body = await result.body.json();
  return body as unknown as PolicyResponse;
}

/** Insurance service health check. */
export async function checkHealth(): Promise<void> {
  const response = await get({
    apiName: API_NAME,
    path: "/api/checkhealth",
  });
  await response.response;
}
